import { FeatureFlags } from '@environments/environment.interface';

/**
 * Static mock flag definition — the single source of truth.
 *
 * This module is a LEAF dependency: it imports only from the environment
 * interface (types only at runtime) and has no value imports from
 * `@environments/environment`. This breaks the circular dependency that
 * would otherwise exist between `src/environments/` and `src/app/core/config/`.
 *
 * GAP-C18 + GAP-FE-033: Centralizes mock flag enumeration. All consumers
 * (interceptor, WS provider, banner, validator, loader) derive from this.
 *
 * To add a new mock flag:
 * 1. Add the flag to FeatureFlags in environment.interface.ts
 * 2. Add a definition here with the appropriate label, affectsWebSocket, and envVarName
 * 3. Set the flag in each environment.*.ts file
 * That's it — all consumers pick it up automatically.
 */
export interface MockFlagDefinition {
  /** Key in FeatureFlags */
  key: keyof FeatureFlags;
  /** Human-readable label for the mock data banner */
  label: string;
  /** Whether this flag controls WebSocket mocking (not just HTTP) */
  affectsWebSocket: boolean;
  /** Canonical UPPER_SNAKE_CASE env var name for runtime override rejection */
  envVarName: string;
}

/**
 * Frozen at runtime to prevent mutation via XSS or malicious extensions.
 * TypeScript's `readonly` is compile-time only; Object.freeze provides runtime protection.
 */
export const MOCK_FLAG_DEFINITIONS: readonly MockFlagDefinition[] = Object.freeze([
  Object.freeze({ key: 'enableMockData' as const, label: 'All API', affectsWebSocket: true, envVarName: 'ENABLE_MOCK_DATA' }),
  Object.freeze({ key: 'enableDashboardMock' as const, label: 'Dashboard', affectsWebSocket: true, envVarName: 'ENABLE_DASHBOARD_MOCK' }),
  Object.freeze({ key: 'enableSystemHealthMock' as const, label: 'System Health', affectsWebSocket: true, envVarName: 'ENABLE_SYSTEM_HEALTH_MOCK' }),
  Object.freeze({ key: 'enableAnomalyDetectionMock' as const, label: 'Anomaly Detection', affectsWebSocket: true, envVarName: 'ENABLE_ANOMALY_DETECTION_MOCK' }),
  Object.freeze({ key: 'enableTenantMock' as const, label: 'Tenant', affectsWebSocket: false, envVarName: 'ENABLE_TENANT_MOCK' }),
  Object.freeze({ key: 'enableSymbolMappingMock' as const, label: 'Symbol Mapping', affectsWebSocket: false, envVarName: 'ENABLE_SYMBOL_MAPPING_MOCK' }),
  Object.freeze({ key: 'enableSchemaMock' as const, label: 'Schema', affectsWebSocket: false, envVarName: 'ENABLE_SCHEMA_MOCK' }),
]);

/** Set of mock flag keys for O(1) membership testing. */
export const MOCK_FLAG_KEYS: ReadonlySet<string> = new Set(
  MOCK_FLAG_DEFINITIONS.map(def => def.key)
);
